import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Brain, Home, Trophy, Users, Target, TrendingUp, ExternalLink, 
  Search, Filter, Clock, Sparkles, ArrowRight, Globe, Award,
  ChevronRight, Zap, BookOpen, Calendar, MapPin
} from 'lucide-react';
import FloatingImpactLabButton from '../components/FloatingImpactLabButton';
import AppFooter from '../components/AppFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// SDG Color mapping
const sdgColors = {
  1: { bg: 'bg-red-500', light: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  2: { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  3: { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  4: { bg: 'bg-red-600', light: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  5: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  6: { bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  7: { bg: 'bg-yellow-500', light: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  8: { bg: 'bg-rose-600', light: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  9: { bg: 'bg-orange-600', light: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  10: { bg: 'bg-pink-600', light: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  11: { bg: 'bg-amber-600', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  12: { bg: 'bg-yellow-700', light: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  13: { bg: 'bg-teal-600', light: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  14: { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  15: { bg: 'bg-green-600', light: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  16: { bg: 'bg-indigo-600', light: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  17: { bg: 'bg-slate-600', light: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
};

// SDG-specific banners — unique generated banner per goal
const sdgImages = {
  1: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/52098243c7b3ba128a323b9ebd9ae71616ab0a7196758b412a6809839de9f293.png',
  2: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/e0d9b9de292188e2ab208988393bf7b3884ae93589c0a4d88942265d8529da2b.png',
  3: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/384be676c36395ef48cc27f81c842003b6822f21a7ef9dfedef4e8cd93b51161.png',
  4: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/4ba955a9eff11a8ff3eb5d9fe4afcf5a35579d708d5880aae0cd89774e8de905.png',
  5: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/b48b388df4a02757b44862d37376986fed178dc6fb4fa28908a524ad2144cde3.png',
  6: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/654ba603680f3e0383e426f1311e8eb86fb703a382e19584ceeba5a44f6acddd.png',
  7: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/5a2d5fb84360ce53685a42d620e34b9909195aa74a352344e1eb5e606c9a3c33.png',
  8: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/c7256f2c7a2342f226ab16dfbd7fc37a7742e97f7bd4a982ba6005cc127ebbc4.png',
  9: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/efb8a2195be1106b25ac90a649d469d541efadec2d646a48e3b7ff9bd8094cf6.png',
  10: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/1bc7a1b22fc42f6f9c11fc2c5405bb216c0f8cb90a78036cc51b7d580c166300.png',
  11: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/36178f64f56d84a897b0f038dda9edc32b8de4980ec638617890428c22f54cc8.png',
  12: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/911edb5c02245155c824484146b23bf0c4e7eae20b0ed554653a462e682e70b6.png',
  13: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/aa8fd68e2afeaf1bc08710b2c2a9f6f3d874db11d4bc03c4dcd3afc8ec46fb7d.png',
  14: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/14332e91caa7d44574ab7bb2cf0cf0a80e8cc356042c37ab62561357a1150548.png',
  15: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/0baa8ba9a05dd8435e1f18b1efd6e446f8be7871f1b3108f016716a935ed8596.png',
  16: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/595edd5cc18ba5497aff035865a9c7ad97df29c07e4687c5f1edf69c62c87b1b.png',
  17: 'https://static.prod-images.emergentagent.com/jobs/2659dccb-abc2-471e-8325-a1c4e2c3c2ac/images/9d74237aa69fba137d42a32054612e491d9976353485f36c6d317d47ecdbb441.png',
};

const Challenges = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSDG, setSelectedSDG] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Fetch challenges from API
  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/challenges`);
        setChallenges(response.data.challenges || response.data || []);
      } catch (error) {
        console.error('Error fetching challenges:', error);
        // Fallback to static data
        setChallenges(staticChallenges);
      } finally {
        setLoading(false);
      }
    };
    fetchChallenges();
  }, []);

  // Static challenges fallback
  const staticChallenges = [
    {
      id: 'sdg1-poverty',
      title: 'End Poverty: Innovative Income Solutions',
      sdg: 1,
      sdg_name: 'No Poverty',
      description: 'Design innovative solutions to create sustainable income opportunities for communities living in extreme poverty.',
      participants: 234,
      submissions: 89,
      deadline: '2025-12-31',
      difficulty: 'Advanced',
      tags: ['Economics', 'Community Development', 'Microfinance'],
      image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800'
    },
    {
      id: 'sdg2-hunger',
      title: 'Zero Hunger: Sustainable Food Systems',
      sdg: 2,
      sdg_name: 'Zero Hunger',
      description: 'Create scalable solutions for sustainable agriculture and food distribution in underserved regions.',
      participants: 412,
      submissions: 156,
      deadline: '2025-11-30',
      difficulty: 'Intermediate',
      tags: ['Agriculture', 'Food Security', 'Supply Chain'],
      image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=800'
    },
    {
      id: 'sdg3-health',
      title: 'Good Health: Accessible Healthcare Tech',
      sdg: 3,
      sdg_name: 'Good Health & Well-being',
      description: 'Develop technology solutions to make healthcare accessible and affordable in remote areas.',
      participants: 567,
      submissions: 203,
      deadline: '2026-01-31',
      difficulty: 'Advanced',
      tags: ['HealthTech', 'Telemedicine', 'Public Health'],
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800'
    },
    {
      id: 'sdg4-education',
      title: 'Quality Education: Learning Equity',
      sdg: 4,
      sdg_name: 'Quality Education',
      description: 'Design innovative learning systems that provide equitable education access globally.',
      participants: 789,
      submissions: 321,
      deadline: '2025-10-31',
      difficulty: 'Intermediate',
      tags: ['EdTech', 'Digital Learning', 'Accessibility'],
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800'
    },
    {
      id: 'sdg7-energy',
      title: 'Clean Energy: Renewable Solutions',
      sdg: 7,
      sdg_name: 'Affordable & Clean Energy',
      description: 'Innovate affordable renewable energy solutions for off-grid communities.',
      participants: 345,
      submissions: 134,
      deadline: '2026-03-31',
      difficulty: 'Advanced',
      tags: ['Clean Energy', 'Solar', 'Sustainability'],
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'
    },
    {
      id: 'sdg13-climate',
      title: 'Climate Action: Carbon Reduction',
      sdg: 13,
      sdg_name: 'Climate Action',
      description: 'Create scalable solutions for carbon reduction and climate adaptation.',
      participants: 923,
      submissions: 412,
      deadline: '2025-09-30',
      difficulty: 'Advanced',
      tags: ['Climate Tech', 'Carbon', 'Adaptation'],
      image: 'https://images.unsplash.com/photo-1569163139599-0f4517e36f51?w=800'
    }
  ];

  const displayChallenges = challenges.length > 0 ? challenges : staticChallenges;

  // Filter challenges
  const filteredChallenges = displayChallenges.filter(challenge => {
    const matchesSearch = challenge.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         challenge.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSDG = selectedSDG ? challenge.sdg === selectedSDG : true;
    return matchesSearch && matchesSDG;
  });

  // Calculate days remaining
  const getDaysRemaining = (deadline) => {
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch(difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const totalParticipants = displayChallenges.reduce((sum, c) => sum + (c.participants || 0), 0);
  const totalSubmissions = displayChallenges.reduce((sum, c) => sum + (c.submissions || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-200">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-orange-500">
              Idea Fountain
            </span>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-violet-600"
          >
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section - Bento Style */}
        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          {/* Main Hero */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-purple-600 to-orange-500 p-8 lg:p-12 text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-400/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
                <Globe className="w-4 h-4" />
                UN SDG Innovation Challenges
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Solve Global Challenges<br />
                <span className="text-orange-200">with Creative Innovation</span>
              </h1>
              
              <p className="text-lg text-white/80 max-w-xl mb-8">
                Apply the Originality Enabling System to tackle UN Sustainable Development Goals. 
                Join thousands of innovators worldwide.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  className="bg-white text-violet-700 hover:bg-white/90 shadow-xl"
                  onClick={() => document.getElementById('challenges-grid').scrollIntoView({ behavior: 'smooth' })}
                >
                  <Target className="w-5 h-5 mr-2" />
                  Browse Challenges
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.open('https://sdgs.un.org/goals', '_blank')}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Learn About SDGs
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Active Innovators</p>
                    <p className="text-3xl font-bold text-slate-900">{totalParticipants.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Solutions Submitted</p>
                    <p className="text-3xl font-bold text-slate-900">{totalSubmissions.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur hover:shadow-xl transition-all duration-300 hover:-translate-y-1 col-span-2 lg:col-span-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-teal-100 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Active Challenges</p>
                    <p className="text-3xl font-bold text-slate-900">{displayChallenges.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search challenges by title or topic..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base border-slate-200 bg-white shadow-sm"
              data-testid="challenge-search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button
              variant={selectedSDG === null ? "default" : "outline"}
              onClick={() => setSelectedSDG(null)}
              className={selectedSDG === null ? "bg-violet-600 hover:bg-violet-700" : ""}
              data-testid="filter-all"
            >
              All SDGs
            </Button>
            {[1, 2, 3, 4, 7, 13].map(sdg => (
              <Button
                key={sdg}
                variant={selectedSDG === sdg ? "default" : "outline"}
                onClick={() => setSelectedSDG(sdg)}
                className={selectedSDG === sdg ? `${sdgColors[sdg]?.bg} hover:opacity-90` : ""}
                data-testid={`filter-sdg-${sdg}`}
              >
                SDG {sdg}
              </Button>
            ))}
          </div>
        </div>

        {/* Challenges Grid - Bento Layout */}
        <div id="challenges-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredChallenges.map((challenge, index) => {
            const colors = sdgColors[challenge.sdg] || sdgColors[1];
            const daysLeft = getDaysRemaining(challenge.deadline);
            const isHovered = hoveredCard === challenge.id;
            
            return (
              <Card
                key={challenge.id}
                className={`group overflow-hidden border-0 shadow-lg bg-white cursor-pointer transition-all duration-500 ${
                  isHovered ? 'shadow-2xl -translate-y-2 scale-[1.02]' : 'hover:shadow-xl'
                } ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                onMouseEnter={() => setHoveredCard(challenge.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => navigate(`/session/new?challenge=${challenge.id}`)}
                data-testid={`challenge-card-${challenge.id}`}
              >
                {/* Image Header */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={challenge.image || sdgImages[challenge.sdg] || sdgImages[1]}
                    alt={challenge.title}
                    className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  
                  {/* SDG Badge */}
                  <div className={`absolute top-4 left-4 ${colors.bg} px-3 py-1.5 rounded-full text-white text-sm font-semibold shadow-lg`}>
                    SDG {challenge.sdg}
                  </div>
                  
                  {/* Days remaining */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {daysLeft} days left
                  </div>
                  
                  {/* Title overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white line-clamp-2 drop-shadow-lg">
                      {challenge.title}
                    </h3>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  {/* SDG Name */}
                  <p className={`text-sm font-medium ${colors.text} mb-3`}>
                    {challenge.sdg_name || challenge.sdgName}
                  </p>
                  
                  {/* Description */}
                  <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                    {challenge.description}
                  </p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(challenge.tags || []).slice(0, 3).map((tag, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary" 
                        className={`${colors.light} ${colors.text} border ${colors.border}`}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {challenge.participants || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Sparkles className="w-4 h-4" />
                        {challenge.submissions || 0}
                      </span>
                    </div>
                    <Badge className={getDifficultyColor(challenge.difficulty)}>
                      {challenge.difficulty || 'Open'}
                    </Badge>
                  </div>
                  
                  {/* CTA Button - appears on hover */}
                  <div className={`mt-4 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                    <Button 
                      className={`w-full ${colors.bg} hover:opacity-90 text-white`}
                      data-testid={`submit-solution-${challenge.id}`}
                    >
                      Submit Your Solution
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredChallenges.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No challenges found</h3>
            <p className="text-slate-500 mb-4">Try adjusting your search or filters</p>
            <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedSDG(null); }}>
              Clear Filters
            </Button>
          </div>
        )}

        {/* How It Works */}
        <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 mb-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-8 text-center">
            How to Participate
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-8 h-8 text-violet-600" />
              </div>
              <div className="text-4xl font-bold text-violet-600 mb-2">1</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Choose a Challenge</h3>
              <p className="text-slate-600 text-sm">
                Browse UN SDG-aligned challenges and pick one that resonates with your expertise
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Zap className="w-8 h-8 text-orange-600" />
              </div>
              <div className="text-4xl font-bold text-orange-600 mb-2">2</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Develop Your Solution</h3>
              <p className="text-slate-600 text-sm">
                Use our AI-guided IF Session to structure and refine your innovative solution
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Award className="w-8 h-8 text-teal-600" />
              </div>
              <div className="text-4xl font-bold text-teal-600 mb-2">3</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Get Recognized</h3>
              <p className="text-slate-600 text-sm">
                Receive OI scores, compete on leaderboards, and gain global recognition
              </p>
            </div>
          </div>
        </div>
      </main>

      <FloatingImpactLabButton />
      <AppFooter />
    </div>
  );
};

export default Challenges;
